// frontend/src/components/quiz/QuizTaker.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Clock, Trophy, Sparkles } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { getQuizTranslation, formatQuizTime } from "../../utils/quizTranslations";
import apiService from "../../services/apiService";
import QuestionCard from "./QuestionCard";
import ConfirmDialog from "../common/ConfirmDialog";

const QuizTaker = ({ quizData, onComplete }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timeSpent, setTimeSpent] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const currentQuestion = quizData.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === quizData.questions.length - 1;
  const showImmediateFeedback = quizData.show_feedback === "immediate";
  const hasAnswered = answers[currentQuestion.id] !== undefined;
  const currentFeedback = feedback[currentQuestion.id];
  const isMultipleSelect = currentQuestion.question_type === "multiple_select";

  // Timer effect
  useEffect(() => {
    if (!quizData.time_limit) return;

    setTimeRemaining(quizData.time_limit);

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [quizData.time_limit]);

  // Track time spent on current question
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [currentQuestionIndex]);

  const handleAnswerSelect = async (selectedAnswerIds, shouldSubmit = true) => {
    const questionId = currentQuestion.id;
    const questionTimeSpent = Math.floor((Date.now() - questionStartTime) / 1000);

    // Update answers state
    setAnswers((prev) => ({
      ...prev,
      [questionId]: selectedAnswerIds,
    }));

    // Update time spent
    setTimeSpent((prev) => ({
      ...prev,
      [questionId]: questionTimeSpent,
    }));

    // Submit answer to backend if immediate feedback is enabled AND we should submit
    // For multiple_select questions, shouldSubmit will be false until user clicks submit button
    if (showImmediateFeedback && shouldSubmit) {
      try {
        const response = await apiService.submitQuizAnswer(
          quizData.attempt_id,
          questionId,
          selectedAnswerIds,
          questionTimeSpent
        );

        if (response.data) {
          setFeedback((prev) => ({
            ...prev,
            [questionId]: response.data,
          }));
        }
      } catch (error) {
        console.error("Error submitting answer:", error);
      }
    }
  };

  const handleSubmitAnswer = async () => {
    const questionId = currentQuestion.id;
    const questionTimeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    const selectedAnswerIds = answers[questionId] || [];

    if (selectedAnswerIds.length === 0) return;

    try {
      const response = await apiService.submitQuizAnswer(
        quizData.attempt_id,
        questionId,
        selectedAnswerIds,
        questionTimeSpent
      );

      if (response.data) {
        setFeedback((prev) => ({
          ...prev,
          [questionId]: response.data,
        }));
      }
    } catch (error) {
      console.error("Error submitting answer:", error);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < quizData.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmitQuizClick = () => {
    if (submitting) return;
    setShowConfirmDialog(true);
  };

  const handleSubmitQuiz = async () => {
    setSubmitting(true);

    try {
      // Submit any unanswered questions if not immediate feedback
      if (!showImmediateFeedback) {
        for (const question of quizData.questions) {
          if (answers[question.id]) {
            await apiService.submitQuizAnswer(
              quizData.attempt_id,
              question.id,
              answers[question.id],
              timeSpent[question.id] || 0
            );
          }
        }
      }

      // Submit the quiz
      const response = await apiService.submitQuiz(quizData.attempt_id);

      if (response.success) {
        // Call onComplete callback
        if (onComplete) {
          onComplete(response.data);
        }
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
      alert(getQuizTranslation("messages.errorSubmittingQuiz", language));
    } finally {
      setSubmitting(false);
    }
  };

  const getProgressPercentage = () => {
    const answeredCount = Object.keys(answers).length;
    return (answeredCount / quizData.questions.length) * 100;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-gray-600">
            {Object.keys(answers).length} {getQuizTranslation("ui.of", language)}{" "}
            {quizData.questions.length} {getQuizTranslation("ui.questions", language)}{" "}
            {getQuizTranslation("ui.answered", language)}
          </p>

          {timeRemaining !== null && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4" />
              <span
                className={
                  timeRemaining < 60 ? "text-red-600 font-bold" : "text-gray-600"
                }
              >
                {formatQuizTime(timeRemaining, language)}
              </span>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-[#04A777] h-2 rounded-full transition-all duration-300"
            style={{ width: `${getProgressPercentage()}%` }}
          ></div>
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <QuestionCard
          question={currentQuestion}
          selectedAnswers={answers[currentQuestion.id] || []}
          onAnswerSelect={handleAnswerSelect}
          onSubmitAnswer={handleSubmitAnswer}
          showFeedback={showImmediateFeedback && currentFeedback}
          feedback={currentFeedback}
          disabled={showImmediateFeedback && currentFeedback}
        />
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {getQuizTranslation("ui.previousQuestion", language)}
        </button>

        {isLastQuestion ? (
          <button
            onClick={handleSubmitQuizClick}
            disabled={submitting || Object.keys(answers).length === 0}
            className="flex items-center gap-2 px-6 py-2 rounded-lg bg-[#EE005F] text-white hover:bg-[#c7004d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                {getQuizTranslation("messages.submittingQuiz", language)}
              </>
            ) : (
              <>
                <Trophy className="w-4 h-4" />
                {getQuizTranslation("ui.submitQuiz", language)}
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleNext}
            disabled={!showImmediateFeedback && !hasAnswered}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00A3E0] text-white hover:bg-[#0082b3] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {getQuizTranslation("ui.nextQuestion", language)}
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleSubmitQuiz}
        title={getQuizTranslation("messages.confirmSubmitTitle", language)}
        message={getQuizTranslation("messages.confirmSubmit", language)}
        confirmText={getQuizTranslation("ui.submitQuiz", language)}
        cancelText={getQuizTranslation("ui.cancel", language)}
        variant="warning"
      />
    </div>
  );
};

export default QuizTaker;
